import { useState } from 'react';
import type { RecordFactView } from '../api.js';

/**
 * The yield-on-cost floor the final-budget re-run tests against (ADR-0069 A8). GGP's is 5.75%; a
 * project may set its own, only stricter (Brad, 2026-10-27). Shown to every reader, because it decides
 * when a protected stop opens; set or released only by the project owner (the server's `basis:lock`).
 *
 * ⚠️ Mirrors `YIELD_ON_COST_FLOOR` and `projectYieldFloor` in `@ggpathos/lifecycle`; `packages/web` has
 * no workspace dependencies, so `YieldFloor.test.tsx` pins the number to the engine's source.
 */
export const GGP_YIELD_FLOOR = 0.0575;

/** Mirrors `YIELD_FLOOR_MAX`: the highest floor the server accepts, as a ratio. */
export const YIELD_FLOOR_MAX = 0.25;

export interface ProjectFloorView {
  readonly floor: number;
  readonly recordedByName: string | null;
  readonly recordedAt: string;
}

/** The project's own floor as the record states it, or null when GGP's governs. Newest statement wins. */
export function projectFloorOf(facts: readonly RecordFactView[]): ProjectFloorView | null {
  let held: { fact: RecordFactView; value: number | null } | null = null;
  for (const f of facts) {
    if (f.kind !== 'deal_fact' || f.key !== 'yieldOnCostFloor') continue;
    const v = f.value as { field?: unknown; value?: unknown } | null;
    if (!v || v.field !== 'yieldOnCostFloor') continue;
    const value = typeof v.value === 'number' && v.value >= GGP_YIELD_FLOOR ? v.value : null;
    if (v.value !== null && value === null) continue; // not a floor the server would honour
    if (!held || f.recordedAt > held.fact.recordedAt) held = { fact: f, value };
  }
  if (!held || held.value === null) return null;
  return {
    floor: held.value,
    recordedByName: held.fact.recordedByName ?? null,
    recordedAt: held.fact.recordedAt,
  };
}

/** 0.065 → "6.50%". */
export const asPercent = (ratio: number) => `${(ratio * 100).toFixed(2)}%`;

/** A typed percent ("6.5") as the ratio the server takes, or null when it is not a number. */
export function percentToRatio(text: string): number | null {
  const n = Number(text.trim().replace(/%$/, ''));
  return text.trim() !== '' && Number.isFinite(n) ? Math.round(n * 100) / 10000 : null;
}

export function YieldFloor({
  facts,
  canSet,
  onSet,
}: {
  facts: readonly RecordFactView[];
  /** Owner only. Absent controls otherwise; the server refuses anyone else regardless. */
  canSet: boolean;
  onSet: (
    floor: number | null,
    reason: string,
  ) => Promise<{ ok: true } | { ok: false; error: string }>;
}) {
  const current = projectFloorOf(facts);
  const [pct, setPct] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const ratio = percentToRatio(pct);
  const tooLow = ratio !== null && ratio < GGP_YIELD_FLOOR;
  // The server refuses above 25%, so say it here in plain words rather than relay its error text.
  const tooHigh = ratio !== null && ratio > YIELD_FLOOR_MAX;
  const submit = async (floor: number | null) => {
    setSaving(true);
    setError(null);
    const r = await onSet(floor, reason.trim());
    setSaving(false);
    if (r.ok) {
      setPct('');
      setReason('');
    } else setError(r.error);
  };

  return (
    <section className="card">
      <h3 className="card__title">Yield-on-cost floor</h3>
      <p>
        {current
          ? `This project's floor is ${asPercent(current.floor)}${current.recordedByName ? `, set by ${current.recordedByName}` : ''} on ${current.recordedAt.slice(0, 10)}. GGP's is ${asPercent(GGP_YIELD_FLOOR)}.`
          : `GGP's floor of ${asPercent(GGP_YIELD_FLOOR)} governs this project.`}{' '}
        After CRP-3, a run whose yield on cost falls below it opens a Stop &amp; Fix. A change takes
        effect on the next run.
      </p>
      {canSet && (
        <form
          className="stack"
          onSubmit={(e) => {
            e.preventDefault();
            if (ratio !== null && !tooLow && !tooHigh) void submit(ratio);
          }}
        >
          <label>
            Set this project&apos;s floor (percent, at or above {asPercent(GGP_YIELD_FLOOR)})
            <input
              inputMode="decimal"
              value={pct}
              onChange={(e) => setPct(e.target.value)}
              placeholder="6.50"
            />
          </label>
          {tooLow && (
            <p className="card__note" role="alert">
              A project&apos;s floor can only be stricter than GGP&apos;s{' '}
              {asPercent(GGP_YIELD_FLOOR)}.
            </p>
          )}
          {tooHigh && (
            <p className="card__note" role="alert">
              A floor can be at most {asPercent(YIELD_FLOOR_MAX)}. Enter it as a percent: 6.5 for
              6.5%.
            </p>
          )}
          <label>
            Why
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} />
          </label>
          <div>
            <button
              type="submit"
              disabled={saving || ratio === null || tooLow || tooHigh || reason.trim() === ''}
            >
              Set floor
            </button>{' '}
            {current && (
              <button
                type="button"
                className="link-btn"
                disabled={saving || reason.trim() === ''}
                onClick={() => void submit(null)}
              >
                Return to GGP&apos;s floor
              </button>
            )}
          </div>
          {error && (
            <p className="card__note" role="alert">
              {error}
            </p>
          )}
        </form>
      )}
    </section>
  );
}
